// Wikilink-aware markdown renderer for brain pages and brain-ask answers.
// [[slug]] references become clickable chips that open the page in the viewer.

import ReactMarkdown from 'react-markdown'

interface Props {
  content: string
  onOpenPage?: (slug: string) => void
}

const WIKILINK_RE = /\[\[([a-z0-9][a-z0-9/_-]{1,118})\]\]/gi
// Fenced code blocks and inline code must keep their [[literal]] text.
const CODE_SPAN_RE = /(```[\s\S]*?```|`[^`\n]*`)/g

// Turn [[slug]] into a normal markdown link with a sentinel href we intercept —
// but only OUTSIDE code spans.
function preprocess(md: string): string {
  return md
    .split(CODE_SPAN_RE)
    .map((chunk, i) =>
      i % 2 === 1 ? chunk : chunk.replace(WIKILINK_RE, (_m, slug: string) => `[${slug}](#brain:${slug.toLowerCase()})`),
    )
    .join('')
}

export function BrainMarkdown({ content, onOpenPage }: Props) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-foreground [&_pre]:overflow-x-auto">
      <ReactMarkdown
        components={{
          h1: ({ children }) => <h1 className="text-lg font-heading font-semibold mt-4 first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-heading font-semibold mt-4 first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-heading font-semibold mt-3 first:mt-0">{children}</h3>,
          p: ({ children }) => <p className="my-2">{children}</p>,
          ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          // Contenido del brain incluye texto de terceros (transcripts): una
          // imagen remota beaconearía la IP de cada lector — solo el alt.
          img: ({ alt }) => <span className="text-xs italic text-muted-foreground">[img: {alt || 'imagen'}]</span>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-border pl-3 text-muted-foreground">{children}</blockquote>
          ),
          code: ({ children }) => (
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="my-2 max-h-64 overflow-auto rounded-lg bg-muted p-3 text-xs">{children}</pre>
          ),
          a: ({ href, children }) => {
            if (href?.startsWith('#brain:')) {
              const slug = href.slice('#brain:'.length)
              return (
                <button
                  type="button"
                  onClick={() => onOpenPage?.(slug)}
                  className="inline-flex items-center rounded bg-teal-500/10 px-1.5 py-0.5 font-mono text-xs text-teal-600 hover:bg-teal-500/20 dark:text-teal-400"
                >
                  {children}
                </button>
              )
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
                {children}
              </a>
            )
          },
        }}
      >
        {preprocess(content)}
      </ReactMarkdown>
    </div>
  )
}
